/**
 * Curriculum API Client
 * 
 * Provides methods to interact with the curriculum backend endpoints.
 * Handles worlds, levels, sections, exercises, and user progress.
 */

import { apiClient } from './client';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/**
 * Get authentication headers
 */
const getHeaders = () => {
  const token = apiClient.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// ===== TYPE DEFINITIONS =====

export interface World {
  id: number;
  world_id: string;
  title: string;
  description: string;
  language: string;
  order: number;
  difficulty: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  icon: string;
  xp_reward: number;
  created_at: string;
  updated_at: string;
}

export interface Level {
  id: number;
  level_id: string;
  world: number;
  world_id?: string;
  title: string;
  description: string;
  order: number;
  estimated_minutes: number;
  grammar_topics: string[];
  vocabulary_themes: string[];
  xp_reward: number;
  created_at: string;
  updated_at: string;
}

export interface Section {
  id: number;
  section_id: string;
  level: number;
  section_type: 'theory' | 'vocabulary' | 'practice' | 'story' | 'flashcard';
  title: string;
  order: number;
  content: any; // JSON field
  exercise_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface Exercise {
  id: number;
  exercise_id: string;
  section: number;
  exercise_type: 'match_pairs' | 'fill_blank' | 'multiple_choice' | 'sentence_builder' | 
                  'listening' | 'flashcard' | 'word_hunt' | 'conversation';
  title: string;
  instructions: string;
  content: any; // JSON field
  order: number;
  difficulty: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  estimated_minutes: number;
  xp_reward: number;
  source: 'yaml' | 'web';
  yaml_hash: string | null;
  modified_in_web: boolean;
  last_modified_by: number | null;
  current_version: number;
  created_at: string;
  updated_at: string;
}

export interface UserWorldProgress {
  id: number;
  user: number;
  world: number;
  is_completed: boolean;
  completed_at: string | null;
  total_xp_earned: number;
  created_at: string;
  updated_at: string;
}

export interface UserLevelProgress {
  id: number;
  user: number;
  level: number;
  is_completed: boolean;
  completed_at: string | null;
  total_xp_earned: number;
  current_section: number | null;
  created_at: string;
  updated_at: string;
}

export interface UserSectionProgress {
  id: number;
  user: number;
  section: number;
  is_completed: boolean;
  completed_at: string | null;
  exercises_completed: number;
  total_exercises: number;
  created_at: string;
  updated_at: string;
}

export interface ExerciseAttempt {
  id: number;
  user: number;
  exercise: number;
  user_answer: any; // JSON field
  is_correct: boolean;
  score: number;
  time_spent_seconds: number;
  completed_at: string;
  feedback?: string;  // Optional feedback message from backend
}

export interface WorldWithProgress extends World {
  progress?: UserWorldProgress;
  total_levels?: number;
  completed_levels?: number;
}

export interface LevelWithProgress extends Level {
  progress?: UserLevelProgress;
  total_sections?: number;
  completed_sections?: number;
  difficulty?: string;
}

export interface SectionWithProgress extends Section {
  progress?: UserSectionProgress;
  exercises?: Exercise[];
}

// ===== API METHODS =====

export const curriculumApi = {
  // ===== WORLDS =====
  
  /**
   * Get all worlds for the user's language
   */
  async getWorlds(): Promise<WorldWithProgress[]> {
    const response = await fetch(`${BASE_URL}/curriculum/worlds/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch worlds: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Get a specific world by ID
   */
  async getWorld(id: number): Promise<WorldWithProgress> {
    const response = await fetch(`${BASE_URL}/curriculum/worlds/${id}/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch world: ${response.statusText}`);
    }
    
    return response.json();
  },

  // ===== LEVELS =====
  
  /**
   * Get all levels for a world
   */
  async getLevelsForWorld(worldId: number): Promise<LevelWithProgress[]> {
    const response = await fetch(`${BASE_URL}/curriculum/worlds/${worldId}/levels/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch levels: ${response.statusText}`);
    }
    
    return response.json();
  },

  /**
   * Get a specific level by ID
   */
  async getLevel(id: number): Promise<LevelWithProgress> {
    const response = await fetch(`${BASE_URL}/curriculum/levels/${id}/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch level: ${response.statusText}`);
    }
    
    return response.json();
  },

  // ===== SECTIONS =====
  
  /**
   * Get all sections for a level
   */
  async getSectionsForLevel(levelId: number): Promise<SectionWithProgress[]> {
    const response = await fetch(`${BASE_URL}/curriculum/levels/${levelId}/sections/`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch sections: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get a specific section by ID
   */
  async getSection(id: number): Promise<Section> {
    const response = await fetch(`${BASE_URL}/curriculum/sections/${id}/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch section: ${response.statusText}`);
    }
    
    return response.json();
  },

  // ===== EXERCISES =====

  /**
   * Get a specific exercise by ID
   */
  async getExercise(id: number): Promise<Exercise> {
    const response = await fetch(`${BASE_URL}/curriculum/exercises/${id}/`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch exercise: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Submit an exercise attempt
   */
  async submitExercise(exerciseId: number, answer: any, timeSpentSeconds: number = 0): Promise<ExerciseAttempt> {
    const response = await fetch(`${BASE_URL}/curriculum/exercises/${exerciseId}/submit/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        answer,
        time_spent_seconds: timeSpentSeconds
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to submit exercise: ${response.statusText}`);
    }

    return response.json();
  },

  // ===== PROGRESS =====
  
  /**
   * Get user's overall curriculum progress
   */
  async getProgress(): Promise<{
    worlds: UserWorldProgress[];
    levels: UserLevelProgress[];
    sections: UserSectionProgress[];
    total_xp: number;
  }> {
    const response = await fetch(`${BASE_URL}/curriculum/progress/`, {
      method: 'GET',
      headers: getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch progress: ${response.statusText}`);
    }
    
    return response.json();
  },
};
